import React, { useEffect, useMemo } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import { generateHref } from 'Helmet/GoustoHelmet'
import Helmet from 'react-helmet'

import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import routesConfig from 'config/routes'

import { BrandEndorsements } from './BrandEndorsements/BrandEndorsements'
import { Carousel } from './Carousel'
import { Hero, ImmersiveHero } from './Hero'
import { HeroCarousel } from './HeroCarousel/HeroCarousel'
import { HighlightChoice } from './HighlightChoice'
import { HowGoustoWorks } from './HowGoustoWorks'
import { PromoBanner } from './PromoBanner'
import { SaveAndSavour } from './SaveAndSavour/SaveAndSavour'
import { TrustPilot } from './TrustPilot/TrustPilot'
import { ValueForMoney } from './ValueForMoney/ValueForMoney'
import { WhyChooseGousto } from './WhyChooseGousto'
import { knownVariants, defaultVariant, seo } from './homeConfig'
import { useGetHomePageExperiments, useGetMenuData } from './homeHooks'

const getHelmetLink = (variant: string) => {
  const {
    client: { home },
  } = routesConfig
  const knownVariant = knownVariants.includes(variant) ? variant : defaultVariant
  const link = knownVariant !== 'default' ? [{ rel: 'canonical', href: generateHref(home) }] : []

  return link
}

type HomePageProps = { location: { query: { variant: string } } }

export const HomePageContent = ({ location }: HomePageProps) => {
  const {
    isHowGoustoWorksEnabled,
    isSaveAndSavourEnabled,
    isImmersiveHeroEnabled,
    isBrandEndorsementsEnabled,
  } = useGetHomePageExperiments()
  const {
    dispatchRedirectLoggedInUser,
    dispatchMenuFetchData,
    dispatchMenuLoadBoxPrices,
    pricePerServing,
  } = useGetMenuData()
  /**
   * Temporary experiment to test allocation for Oprimizely FX implementation
   */
  useDecision('fef_ofx_homepage_test')

  dispatchRedirectLoggedInUser()

  useEffect(() => {
    if (!pricePerServing) dispatchMenuLoadBoxPrices()

    const prefetchTimer = setTimeout(() => {
      dispatchMenuFetchData()
      clearTimeout(prefetchTimer)
    }, 500)

    return () => {
      clearTimeout(prefetchTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const link = useMemo(() => getHelmetLink(location.query.variant), [location.query.variant])

  return (
    <>
      <Helmet
        title={seo.title}
        meta={seo.meta}
        link={link}
        style={[
          {
            cssText: `
              #react-root {
                height: 100%;
              }
            `,
          },
        ]}
      />

      <>
        <PromoBanner />
        <section data-module-name="">
          {isImmersiveHeroEnabled ? <ImmersiveHero /> : <Hero />}
        </section>
        <section data-module-name="">
          {isImmersiveHeroEnabled ? <HeroCarousel /> : <HighlightChoice />}
        </section>
        {isHowGoustoWorksEnabled && (
          <section data-module-name="">
            <HowGoustoWorks />
          </section>
        )}
        <section data-module-name="">
          <TrustPilot />
        </section>
        <section data-module-name="">
          <ValueForMoney />
        </section>
        {isBrandEndorsementsEnabled && (
          <section data-module-name="">
            <BrandEndorsements />
          </section>
        )}
        <section data-module-name="">
          <WhyChooseGousto />
        </section>
        <section data-module-name="">
          <Carousel />
        </section>
        {isSaveAndSavourEnabled && (
          <section data-module-name="">
            <SaveAndSavour />
          </section>
        )}
      </>

      <RibbonTriggerContainer name="home-page" />
    </>
  )
}
